import { getItemPrice } from '@next-order/next-order-engine';

/**
 * Item voucher (type "3") — cart matching.
 *
 * Single source of the type 3 matching rules, shared by updateVoucher in
 * containers/Home (when the voucher is applied) and engineGetOrderCalculations
 * (re-matching non-loyalty vouchers on every calculation, so eligible items
 * added straight from the menu get the discount too).
 *
 * Matching rules:
 * - each voucher category has `quantity` slots, filled by that category's
 *   active menu items whose size (or sub-modifier) the category allows;
 * - lines already tagged with this voucher (popup picks, auto-added item)
 *   are kept first so the user's explicit choices stay discounted;
 * - remaining slots are filled by untagged eligible lines in cart order.
 */

const getParentModifierIdForSubModifier = (menuItem, subModifierId) => {
  if (!menuItem?.selectedModifiers || !subModifierId) return '';

  for (const modifier of menuItem.selectedModifiers) {
    if (
      modifier?.subModifiers?.some(
        (subModifier) => subModifier?._id === subModifierId
      )
    ) {
      return modifier?._id || '';
    }
  }

  return '';
};

export const isVoucherSizeAllowedForItem = (
  menuItem,
  sizeId,
  activeSizeIds,
  activeSubModifiers
) => {
  if (activeSizeIds.size === 0 && activeSubModifiers.length === 0) {
    // No explicit voucher size/submodifier constraints means "any size".
    return true;
  }
  if (!sizeId) return false;
  if (activeSizeIds.has(sizeId)) return true;

  const parentModifierId = getParentModifierIdForSubModifier(menuItem, sizeId);
  return activeSubModifiers.some((subModifier) => {
    if (subModifier?._id !== sizeId) return false;
    if (!subModifier?.modifierId) return true;
    return parentModifierId && subModifier.modifierId === parentModifierId;
  });
};

/** Active size ids and sub-modifiers allowed across `categories`. */
export const getVoucherSizeRules = (categories) => ({
  activeSizeIds: new Set(
    (categories || [])
      .flatMap((category) => category?.menuSizes || [])
      .filter((size) => size?.isActive === true)
      .map((size) => size._id)
  ),
  activeSubModifiers: (categories || [])
    .flatMap((category) => category?.subModifiers || [])
    .filter((subModifier) => subModifier?.isActive === true),
});

/**
 * Categories listing `menuItemId`, or every category when none does — the
 * size rules a single-item voucher's item is matched against.
 */
export const getCategoriesForMenuItem = (voucher, menuItemId) => {
  const categories = voucher?.selectedCategories || [];
  const categoriesForItem = categories.filter((category) =>
    (category?.menuItems || []).some((menuItem) => menuItem?._id === menuItemId)
  );
  return categoriesForItem.length > 0 ? categoriesForItem : categories;
};

/** Total slots across all voucher categories. */
export const getVoucherSlotCount = (voucher) =>
  (voucher?.selectedCategories || []).reduce(
    (sum, category) => sum + (Number(category?.quantity) || 0),
    0
  );

const getCategoryRules = (category) => ({
  slots: Number(category?.quantity) || 0,
  menuItemIds: new Set(
    (category?.menuItems || [])
      .filter((menuItem) => menuItem?.isActive)
      .map((menuItem) => menuItem._id)
  ),
  ...getVoucherSizeRules([category]),
});

const isLineEligibleForCategory = (item, rules) =>
  rules.menuItemIds.has(item?._id) &&
  (item.selectedSizes || []).some((size) =>
    isVoucherSizeAllowedForItem(
      item,
      size?._id,
      rules.activeSizeIds,
      rules.activeSubModifiers
    )
  );

/**
 * Tags `menuItems` in place (voucherId / isVoucherItem) and returns the
 * voucher items with quantities capped to the slots they fill — the shape
 * updateVoucher dispatches as userSelectedVoucherItems.
 */
export const matchType3VoucherItems = (menuItems, voucher, halfHalfSetup) => {
  const categories = (voucher?.selectedCategories || []).map(getCategoryRules);
  const wasTagged = new Set();

  (menuItems || []).forEach((item, index) => {
    if (item.voucherId === voucher._id) wasTagged.add(index);
    delete item.voucherId;
    delete item.isVoucherItem;
  });

  const voucherItems = [];
  const claimLine = (item, rules) => {
    const quantity = Math.min(Number(item.quantity) || 0, rules.slots);
    if (quantity <= 0) return;
    const itemPrice = Number(getItemPrice(item, halfHalfSetup));
    item.voucherId = voucher._id;
    item.isVoucherItem = true;
    voucherItems.push({
      ...item,
      quantity,
      itemPrice: quantity * (itemPrice / item.quantity),
    });
    rules.slots -= quantity;
  };

  // Previously tagged lines first, then untagged ones fill what is left.
  [true, false].forEach((taggedPass) => {
    (menuItems || []).forEach((item, index) => {
      if (item.voucherId) return;
      if (wasTagged.has(index) !== taggedPass) return;
      const rules = categories.find(
        (category) =>
          category.slots > 0 &&
          (taggedPass
            ? category.menuItemIds.has(item?._id)
            : isLineEligibleForCategory(item, category))
      );
      if (rules) claimLine(item, rules);
    });
  });

  return voucherItems;
};
